import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import classNames from 'classnames';

import ProductColors from './ProductColors/ProductColors';
import ProductPrice, { ProductPriceClasses } from './ProductPrice/ProductPrice';
import ProductQuantity from './ProductQuantity/ProductQuantity';
import ProductRating from './ProductRating/ProductRating';
import ProductStickersAdapter from './ProductStickers/ProductStickersAdapter';
import ProductAddBasket, {
  ProductAddBasketClasses,
} from './ProductAddBasket/ProductAddBasket';
import ProductSizes from './ProductSizes/ProductSizes';
import ProductStickyBar from './ProductStickyBar/ProductStickyBar';
import ProductSizeGuide from './ProductSizeGuide/ProductSizeGuide';
import ProductBrand from './ProductBrand/ProductBrand';
import ProductOneyBanner from './ProductOneyBanner/ProductOneyBanner';
import ModalAddToMyBasket from 'components/Modals/ModalAddToMyBasket/ModalAddToMyBasket';
import InsertHtmlContent from 'components/Common/InsertHtmlContent/InsertHtmlContent';
import useHistory from 'hooks/useHistory';
import { useProductSku } from 'hooks/ProductPageV2/useProductSku';
import { useProductStocks } from 'hooks/ProductPageV2/useProductStocks';
import { useModalMultiples } from 'hooks/useModal';
import useIsEditProduct from 'hooks/ProductPageV2/useIsEditProduct';
import useURLQuery from 'hooks/useURLQuery';
import { Cart } from 'types/Cart';
import { STOCK_STATUS_NOT_AVAILABLE } from 'types/ProductPageV2/ProductsStocks';
import { getProductData } from 'services/gtm/gtmPageView';
import { formatProductV2 } from 'services/gtm/gtmUtils';
import { pushGtmEvent } from 'services/gtm/gtmEvents';
import { capitalize } from 'services/formatter';
import {
  getBrandName,
  getColor,
  getLot,
  getName,
  getRealSku,
  getSizeGuide,
} from 'services/product';
import { isBot } from 'services/ua';
import { TABLET_PORTRAIT_MAX } from 'settings/device';
import {
  ProductColorsType,
  ProductKind,
  ProductType,
  PRODUCT_TYPE,
  ProductVariantsSkusType,
  SelectedProductAttributes,
} from 'types/ProductPageV2/Products';
import { ProductReviewsRatingType } from 'types/ProductPageV2/ProductReviews';

import styles from './ProductDetails.module.scss';

function ProductDetails({
  product,
  productAttributes,
  skus,
  type,
  colors,
  sizes,
  cups,
  color,
  setColor,
  rating,
  offer,
  isMCProduct,
}: {
  product: ProductType;
  productAttributes: SelectedProductAttributes;
  skus: ProductVariantsSkusType;
  type: ProductKind;
  colors: ProductColorsType[];
  sizes: string[];
  cups: string[];
  color: string | undefined;
  setColor: (color: string) => void;
  rating: ProductReviewsRatingType | undefined;
  offer: string | undefined;
  isMCProduct: boolean | undefined;
}): JSX.Element {
  const isMaxTablet = useMediaQuery({ maxWidth: TABLET_PORTRAIT_MAX });
  const navigate = useNavigate();
  const { changeColorCounter, previousPath, previousPage } = useHistory();
  const { selected } = useURLQuery();
  const [size, setSize] = useState<string>();
  const [cup, setCup] = useState<string>();
  const [qty, setQty] = useState<number>(1);
  const addButtonRef = useRef<null | HTMLButtonElement>(null);
  const { open, openModal, closeModal } = useModalMultiples();
  const {
    codeCouleur,
    prix: price,
    prixBarre: preDiscountPrice,
    ecopart,
    prixVolume: priceVolume,
    decote: discountPercentage,
    prixUnique: uniquePrice,
    nomModele: modelName,
    SKUReelle: realSku,
    review,
    metaMarque,
    codeLibelleFamille,
    codeFamilleSsfamille,
    codeLibelleSsfamille,
    subuniverse,
    universe,
    PRIXROUGE: redPrice,
  } = productAttributes;

  const brandName = getBrandName(productAttributes);
  const { editUrl } = useIsEditProduct();
  const {
    stocks,
    isLoaded: stockIsLoaded,
    getStockStatusBySKU,
  } = useProductStocks(codeCouleur.Value);
  const { selectedProduct } = useProductSku(product, skus, type, size, cup);
  const sku = selectedProduct && getRealSku(selectedProduct.Attributes);
  const sizeGuide = getSizeGuide(productAttributes);
  const gtmMandatoryAttributes = {
    noteProduit: review,
    metaMarque: metaMarque,
    codeLibelleFamille: codeLibelleFamille,
    codeLibelleSsfamille: codeLibelleSsfamille,
    codeFamilleSsfamille: codeFamilleSsfamille,
    subuniverse: subuniverse,
    universe: universe,
  };
  const formattedGtmProductData = getProductData(
    formatProductV2(productAttributes)
  );
  function handleGtmEvent(cart: Cart) {
    pushGtmEvent(
      {
        event: '_add_to_cart',
        products: [
          {
            ...formattedGtmProductData,
            add_to_cart_type: 'fiche produit',
            quantity: qty,
          },
        ],
      },
      cart
    );
  }

  // Handle pre-selected product
  useEffect(() => {
    if (selected || (isBot() && !isMCProduct)) {
      const { bonnet, codeTaille } = product.Attributes;
      codeTaille && setSize(codeTaille.Value);
      bonnet && setCup(bonnet.Value);
    }
  }, []);

  useEffect(() => {
    if (!stockIsLoaded || !stocks) {
      return;
    }

    if (!sku) {
      size && setSize(undefined);
      cup && setCup(undefined);

      return;
    }

    const stockStatus = getStockStatusBySKU(sku, stocks);

    if (
      stockStatus === STOCK_STATUS_NOT_AVAILABLE ||
      undefined === stockStatus
    ) {
      size && setSize(undefined);
      cup && setCup(undefined);
    }
  }, [color, stockIsLoaded]);

  const renderAddBasket = (
    onSizeRequired?: boolean,
    elementRef?: MutableRefObject<null | HTMLButtonElement>,
    classes?: Partial<ProductAddBasketClasses>
  ): JSX.Element => {
    return (
      <ProductAddBasket
        product={selectedProduct}
        qty={qty}
        sendGtmEvent={handleGtmEvent}
        onSizeRequired={onSizeRequired}
        elementRef={elementRef}
        openModal={openModal}
        closeModal={closeModal}
        open={open}
        gtmMandatoryAttributes={gtmMandatoryAttributes}
        classes={classes}
        disabled={
          !stocks ||
          (type === PRODUCT_TYPE.PRODUCT_UNIQUE &&
            stocks?.[0].state === STOCK_STATUS_NOT_AVAILABLE)
        }
      />
    );
  };

  const renderPrice = (
    mode = 'full',
    classes?: Partial<ProductPriceClasses>
  ): JSX.Element => {
    return (
      <ProductPrice
        price={selectedProduct?.Attributes.prix.Value ?? price?.Value}
        preDiscountPrice={
          selectedProduct?.Attributes.prixBarre.Value ?? preDiscountPrice?.Value
        }
        discountPercentage={
          selectedProduct?.Attributes.decote.Value ?? discountPercentage?.Value
        }
        selectedSize={size}
        ecopart={ecopart?.Value}
        priceVolume={priceVolume?.Value}
        isUniquePrice={uniquePrice.Value == '1'}
        liteMode={mode === 'lite'}
        classes={classes}
        redPrice={redPrice?.Value}
        lot={getLot(productAttributes)}
      />
    );
  };

  /**
   * Add no margin class to product color container by condition
   */
  const isProductColorNoMargin = (): boolean => {
    return !isMaxTablet && getSizeGuide(productAttributes) > 0 && type !== 0;
  };

  return (
    <>
      {brandName && (
        <ProductBrand
          classes={{ container: styles.productBrand }}
          brandName={brandName}
          brandStore={product.BrandHierarchyUrl}
        />
      )}
      <div className={styles.productTitle}>
        <h1 className={styles.title}>
          {getName(productAttributes)}
          <br />
          {capitalize(getColor(productAttributes))}
        </h1>
        {rating && (
          <ProductRating
            rating={rating}
            modelName={modelName.Value}
            realSku={realSku.Value}
          />
        )}
      </div>
      <ProductStickersAdapter
        productAttributes={productAttributes}
        classes={{ container: styles.productStickers }}
        onClick={(label: string) => {
          pushGtmEvent({
            event: '_click_other',
            category: 'clic autre - fiche produit',
            element: 'sticker',
            detail: label.toLowerCase(),
          });
        }}
      />
      <div className={styles.productPrice}>
        {renderPrice('full', {
          productPriceContainer: classNames({
            [styles.hide]: isMaxTablet,
          }),
        })}
      </div>
      <div className={classNames(styles.oneyBanner, styles.onlyDesktop)}>
        <ProductOneyBanner
          price={parseFloat(
            selectedProduct?.Attributes.prix.Value ?? price?.Value ?? 0
          )}
        />
      </div>
      {offer && (
        <InsertHtmlContent
          htmlContent={offer}
          className={styles.offer}
          onClick={() =>
            pushGtmEvent({
              event: '_click_other',
              category: 'clic autre - fiche produit',
              element: 'offre produit',
              products: [{ ...formattedGtmProductData, quantity: qty }],
            })
          }
        />
      )}
      <div className={classNames(styles.oneyBanner, styles.onlyMobile)}>
        <ProductOneyBanner
          price={parseFloat(
            selectedProduct?.Attributes.prix.Value ?? price?.Value ?? 0
          )}
        />
      </div>
      <div
        className={classNames(styles.productColors, {
          [styles['no-margin']]: isProductColorNoMargin(),
        })}
      >
        <ProductColors
          colors={colors}
          color={color}
          setColor={(color, e) => {
            e.preventDefault();
            pushGtmEvent({
              event: '_click_other',
              category: 'clic autre - fiche produit',
              element: 'couleur',
              detail: color.toLowerCase(),
              products: [{ ...formattedGtmProductData, quantity: qty }],
            });
            setColor(color);
            navigate(e.currentTarget.getAttribute('href') as string, {
              state: {
                historyPreviousPage:
                  changeColorCounter === 0 ? previousPath : previousPage,
                historyChangeColorCounter: changeColorCounter + 1,
              },
            });
          }}
          editUrl={editUrl}
          maxItems={6}
          breakpoints={{ 1200: 8 }}
        />
      </div>
      <div
        className={classNames(
          styles.productSizes,
          type === 0 && sizeGuide > 0 && styles.noMargin
        )}
      >
        <ProductSizes
          skus={skus}
          type={type}
          sizes={sizes}
          cups={cups}
          size={size}
          setSize={size => {
            pushGtmEvent({
              event: '_click_other',
              category: 'clic autre - fiche produit',
              element: 'taille',
              detail: 'taille validée',
              products: [{ ...formattedGtmProductData, quantity: qty }],
            });
            setSize(size);
          }}
          cup={cup}
          setCup={setCup}
          open={open === 'productSize'}
          closeModal={closeModal}
          renderAddBasket={renderAddBasket}
          stocks={stocks}
          stockIsLoaded={stockIsLoaded}
          onSelectOpen={() => {
            pushGtmEvent({
              event: '_click_other',
              category: 'clic autre - fiche produit',
              element: 'taille',
              products: [{ ...formattedGtmProductData, quantity: qty }],
            });
          }}
        />
        {sizeGuide > 0 && (
          <ProductSizeGuide
            className={classNames(
              styles.sizeGuideLinkContainer,
              type === 0 && styles.isUniqSize
            )}
            onClick={() => {
              pushGtmEvent({
                event: '_click_other',
                category: 'clic autre - fiche produit',
                element: 'guide des tailles',
              });
            }}
          />
        )}
      </div>
      {!isMaxTablet && (
        <div className={styles.qtyBasket}>
          <ProductQuantity
            qty={qty}
            setQty={setQty}
            classes={{ container: styles.qty }}
          />
          <div className={styles.basket}>
            {renderAddBasket(true, addButtonRef)}
          </div>
        </div>
      )}
      <ProductStickyBar
        addButtonRef={addButtonRef}
        stickyBarClassName={styles.stickyBar}
        contentClassName={styles.content}
      >
        <p className={styles.title}>
          {brandName && <span>{brandName}&#174; - </span>}
          {getName(productAttributes)}
          <br />
          {capitalize(getColor(productAttributes))}
        </p>
        <div className={styles.priceBlock}>
          {renderPrice('lite', {
            price: styles.price,
            labelFrom: styles.labelFrom,
            discountContainer: styles.discountContainer,
            productPriceContainer: styles.productPriceContainer,
            finalPriceContainer: styles.finalPriceContainer,
          })}
        </div>
        <div className={styles.addButton}>
          {renderAddBasket(true, undefined, {
            addToCartButton: styles.addToCartButton,
          })}
        </div>
      </ProductStickyBar>
      {selectedProduct && open === 'addToMyBasket' && (
        <ModalAddToMyBasket
          closeModal={closeModal}
          product={selectedProduct}
          enableMerch
          isOpen
        />
      )}
    </>
  );
}

export default ProductDetails;
